import { matchRoutes, RouteObject } from "react-router-dom";

import { UserDto } from "../../DTOs/User/UserDto";
import { AreaKey, isAreaKey, ViewKey } from "./pageVocabulary.generated";
import { hasAdminRights, hasArea, hasLeadAdminRights } from "./roleUtilities";

export interface RouteRequirements {
  view?: ViewKey;
  alsoOpenTo?: readonly AreaKey[];
  adminOnly?: boolean;
  leadAdminOnly?: boolean;
  openToLeadAdmins?: boolean;
}

/**
 * A page's access area is its view, when that view is one a zone can be granted. Views that
 * are not grantable (Users, Mine Periods, Page Access) are gated by privilege role or site-wide
 * admin instead, which is why this returns undefined rather than failing.
 */
export const routeArea = (view: ViewKey | undefined): AreaKey | undefined =>
  isAreaKey(view) ? view : undefined;

export const declaresRequirements = (route: RouteRequirements) =>
  !!(routeArea(route.view) || route.adminOnly || route.leadAdminOnly || route.openToLeadAdmins);

type RouteTree = RouteRequirements & { path?: string; children?: RouteTree[] };

const toRouteObjects = (routes: RouteTree[]): RouteObject[] =>
  routes.map(route => ({
    path: route.path,
    handle: route,
    children: route.children && toRouteObjects(route.children),
  }));

/** The routes a path passes through, outermost first, as they were declared. */
export const routeChainAt = <Route extends RouteTree>(routes: Route[], pathname: string): Route[] =>
  (matchRoutes<RouteObject>([{ path: "/", children: toRouteObjects(routes) }], pathname) ?? [])
    .flatMap(match => match.route.handle ? [match.route.handle as Route] : []);

/** The page at the end of a chain: the deepest route on it that names a view. */
export const pageOf = <Route extends RouteRequirements>(chain: Route[]): Route | undefined =>
  chain.findLast(level => level.view);

/** A Lead Admin on a page open to them, which they reach and edit for the part of it they administer. */
export const administersAsLeadAdmin = (route: RouteRequirements, user: UserDto | null): boolean =>
  !!route.openToLeadAdmins && hasLeadAdminRights(user);

export function satisfiesRouteRequirements(route: RouteRequirements, user: UserDto | null): boolean {
  if (!declaresRequirements(route)) return true;
  if (!user) return false;
  if (administersAsLeadAdmin(route, user)) return true;

  const area = routeArea(route.view);

  return (!area || hasArea(user, area) || !!route.alsoOpenTo?.some(other => hasArea(user, other)))
    && (!route.adminOnly || hasAdminRights(user))
    && (!route.leadAdminOnly || hasLeadAdminRights(user));
}
